import { DomainCode, logger } from '@parrot-software/observability';

/**
 * Descriptor of a remote microfrontend consumed by the shell.
 * One instance per MFE lives under `src/mfe/remotes/<name>/config.ts`.
 */
export type RemoteConfig = {
  /** Observability domain code used when logging load events (e.g. `INV`). */
  domain: DomainCode;
  /** Base URL of the remote deployment, from a `REACT_APP_*` env var. */
  envUrl?: string;
  /** Module Federation remote name — must match the remote build's `name`. */
  name: string;
  /** Query param that overrides the URL at runtime (QA/previews). */
  overrideParam: string;
};

const storageKey = (name: string): string => `mfe-url:${name}`;

const LOCALHOST_ORIGIN = /^https?:\/\/localhost(:\d+)?$/;

const originOf = (url: string): string | null => {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
};

/**
 * Origins allowed to serve remotes in this environment, from the comma-separated
 * `REACT_APP_MFE_ALLOWED_ORIGINS`. Read on every call so tests can change it.
 */
export const getAllowedRemoteOrigins = (): string[] =>
  (process.env.REACT_APP_MFE_ALLOWED_ORIGINS ?? '')
    .split(',')
    .map((origin) => origin.trim().replace(/\/$/, ''))
    .filter(Boolean);

const escapeRegExp = (value: string): string => value.replace(/[.+?^${}()|[\]\\]/g, '\\$&');

/**
 * An entry matches its exact origin. A `*` stands for a fragment of a single DNS label (letters,
 * digits and hyphens, never a dot), the same rule the remote and the API apply. That covers both
 * Vercel preview shapes, by hash and by branch (`app-git-feat-x-team`), without letting a deeper
 * subdomain or another domain slip through.
 */
const matchesAllowedOrigin = (origin: string, entry: string): boolean => {
  if (!entry.includes('*')) {
    return origin === entry;
  }
  const pattern = entry.split('*').map(escapeRegExp).join('[a-z0-9-]+');
  return new RegExp(`^${pattern}$`).test(origin);
};

const isShellOnLocalhost = (): boolean => window.location.hostname === 'localhost';

/**
 * Whether an origin may serve remote code inside the shell's session. Only the explicit
 * allowlist passes, plus localhost while the shell itself runs on localhost (local dev).
 */
export const isAllowedRemoteOrigin = (url: string): boolean => {
  const origin = originOf(url);
  if (!origin) {
    return false;
  }
  if (getAllowedRemoteOrigins().some((entry) => matchesAllowedOrigin(origin, entry))) {
    return true;
  }
  return isShellOnLocalhost() && LOCALHOST_ORIGIN.test(origin);
};

/**
 * Whether the host may register a remote from this URL. Until the environment declares its
 * allowlist, the build-time env URL is trusted as is (overrides are still filtered on read), so a
 * deploy without the variable keeps working instead of losing the module.
 */
export const isRemoteUrlAllowed = (url: string): boolean =>
  getAllowedRemoteOrigins().length === 0 || isAllowedRemoteOrigin(url);

/**
 * The runtime override is a QA tool; production never honors it, whatever the URL, so a crafted
 * link can never swap the code a real customer runs.
 */
const isOverrideEnabled = (): boolean => process.env.REACT_APP_ENV !== 'production';

/**
 * Reads a runtime URL override for a remote: `?<overrideParam>=<url>` is captured once and
 * persisted in sessionStorage so it survives in-app navigation. Only honored outside production,
 * when the remote is already enabled via env var, and when the override origin passes the
 * allowlist, both on write and on read, so persisted values are re-validated and purged if invalid.
 */
const readOverrideUrl = ({ domain, envUrl, name, overrideParam }: RemoteConfig): string | null => {
  if (!envUrl) {
    return null;
  }

  try {
    const fromQuery = new URLSearchParams(window.location.search).get(overrideParam);
    if (!isOverrideEnabled()) {
      if (fromQuery) {
        logger.forDomain(domain).warn('mfeRemoteOverride', { reason: 'disabledInProduction', remote: name });
      }
      return null;
    }
    if (fromQuery && isAllowedRemoteOrigin(fromQuery)) {
      window.sessionStorage.setItem(storageKey(name), fromQuery);
    } else if (fromQuery) {
      logger.forDomain(domain).warn('mfeRemoteOverride', {
        overrideOrigin: originOf(fromQuery) ?? 'invalid',
        reason: 'originNotAllowed',
        remote: name,
      });
    }

    const stored = window.sessionStorage.getItem(storageKey(name));
    if (stored && !isAllowedRemoteOrigin(stored)) {
      window.sessionStorage.removeItem(storageKey(name));
      return null;
    }
    return stored;
  } catch {
    return null;
  }
};

/**
 * Resolves the effective base URL for a remote, or null when the remote is
 * disabled for this environment (callers should render the legacy module).
 */
export const resolveRemoteUrl = (config: RemoteConfig): string | null => {
  if (!config.envUrl) {
    return null;
  }

  const resolved = readOverrideUrl(config) || config.envUrl;
  return resolved.replace(/\/$/, '');
};

/**
 * Whether a remote microfrontend should replace its legacy module
 * for this session.
 */
export const isRemoteEnabled = (config: RemoteConfig): boolean => Boolean(resolveRemoteUrl(config));
